import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useLocalization } from "@fluent/react";
import {
  LoadingSkeleton,
  Row,
  Toggle,
  Tooltip,
  Button,
} from "@narmi/design_system";
import ReactMarkdown from "react-markdown";
import { ContentCard } from "cerulean";
import Organization from "byzantine/src/Organization";
import OrganizationUser from "byzantine/src/OrganizationUser";
import InstitutionSettingsContext, {
  InstitutionSettingsContextProvider,
} from "../contexts/InstitutionSettingsContext";
import ConfirmModal from "../ConfirmModal";
import styles from "./DualApproval.module.scss";
import ApprovalExpirationDialog from "./ApprovalExpirationDialog";

const APPROVAL_TYPES = {
  WIRE: {
    key: "dual_approval_required",
    apiMethod: "dualApprovalRequired",
    label: "Domestic wires",
    testId: "dual-approval-confirm",
    featureKey: "wires",
  },
  INTERNATIONAL_WIRE: {
    key: "international_wire_dual_approval_required",
    apiMethod: "setOrganization",
    label: "International wires",
    testId: "international-wire-dual-approval-confirm",
    featureKey: "international_wires",
  },
};

const DualApprovalOptIn = () => {
  const { l10n } = useLocalization();
  const [orgUuid, setOrgUuid] = useState(undefined);
  const [orgRole, setOrgRole] = useState(undefined);
  const [organization, setOrganization] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [approvalTypes, setApprovalTypes] = useState(APPROVAL_TYPES);
  const [showApprovalExpirationModal, setShowApprovalExpirationModal] =
    useState(false);
  const {
    short_name,
    features,
    max_days_approval_grace_period: maxDays,
  } = useContext(InstitutionSettingsContext);

  const { ach_payments: achEnabled, expedited_payments: expeditedEnabled } =
    features || {};

  // load org on mount and store it in the DOM (if not already populated)
  useEffect(() => {
    const bootstrapOrgData = async () => {
      setLoading(true);
      const currentOrgUser = await OrganizationUser.currentOrgUser();

      // check if member of an org
      if (!currentOrgUser) {
        setLoading(false);
        return;
      }

      setOrgUuid(currentOrgUser.organization_uuid);
      setOrgRole(currentOrgUser.role.name);

      // load dualApproval status
      const org = await Organization.dualApprovalRequired(
        currentOrgUser.organization_uuid
      );

      // load achDualApprovalStatus
      const orgDetails = await Organization.getOrganization(
        currentOrgUser.organization_uuid
      );

      // load if org has multiple users
      const { organization_users: orgUsers } =
        await OrganizationUser.forOrganization(
          currentOrgUser.organization_uuid
        );

      const hasMultipleUsers = orgUsers.length > 1;

      const {
        ach_dual_approval_required,
        international_wire_dual_approval_required,
        ach_dual_approval_expiration_grace_period_days,
      } = orgDetails;

      const updatedOrg = {
        ...org,
        ach_dual_approval_required,
        international_wire_dual_approval_required,
        ach_dual_approval_expiration_grace_period_days,
        hasMultipleUsers,
      };

      setOrganization(() => updatedOrg);
      setLoading(false);
    };
    bootstrapOrgData();
  }, []);

  useEffect(() => {
    const updatedTypes = { ...APPROVAL_TYPES };

    if (achEnabled && !expeditedEnabled) {
      updatedTypes.ACH = {
        key: "ach_dual_approval_required",
        apiMethod: "setOrganization",
        label: "ACH payments",
        testId: "ach-dual-approval-confirm",
        featureKey: "ach_payments",
      };
    } else if (achEnabled && expeditedEnabled) {
      updatedTypes.ACH_AND_EXPEDITE = {
        key: "ach_dual_approval_required",
        apiMethod: "setOrganization",
        label: "ACH and instant payments",
        testId: "ach-dual-approval-confirm",
        featureKey: "expedited_payments",
      };
    }

    setApprovalTypes(updatedTypes);
  }, [achEnabled, expeditedEnabled]);

  const showModal = (type) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  const toggleApproval = async (approvalType) => {
    const config = approvalTypes[approvalType];

    if (config.apiMethod === "dualApprovalRequired") {
      const org = await Organization.dualApprovalRequired(
        orgUuid,
        "POST",
        !organization[config.key]
      );
      const updatedOrg = {
        ...organization,
        dual_approval_required: org.dual_approval_required,
        dual_approval_user_control: org.dual_approval_user_control,
      };
      setOrganization(() => updatedOrg);
      return updatedOrg;
    } else {
      const orgDetails = await Organization.setOrganization(orgUuid, "PATCH", {
        [config.key]: !organization[config.key],
      });
      const updatedOrg = {
        ...organization,
        [config.key]: orgDetails[config.key],
        // if toggling approval on, set dual_approval_user_control to false so user can't toggle off
        ...(orgDetails[config.key] && { dual_approval_user_control: false }),
      };
      setOrganization(() => updatedOrg);
      return updatedOrg;
    }
  };

  const handleToggleApproval = (approvalType) => {
    const config = approvalTypes[approvalType];

    // can't turn off unless dual_approval_user_control is enabled
    if (organization[config.key] && !organization.dual_approval_user_control) {
      return false;
    }

    if (!organization[config.key]) {
      return showModal(approvalType);
    }
    return toggleApproval(approvalType);
  };

  const handleConfirmToggle = async (approvalType) => {
    await toggleApproval(approvalType);
    closeModal();
  };

  const handleToggleExpirationModal = () => {
    setShowApprovalExpirationModal(!showApprovalExpirationModal);
  };

  const handleSaveExpirationDays = async (days) => {
    const org = await Organization.setOrganization(orgUuid, "PATCH", {
      ach_dual_approval_expiration_grace_period_days: days,
    });
    const updatedOrg = {
      ...organization,
      ach_dual_approval_expiration_grace_period_days:
        org.ach_dual_approval_expiration_grace_period_days,
    };

    setOrganization(updatedOrg);
  };

  const hasDualApprovals = features && features.dual_approval;

  // if user doesn't have feature flag for any dual approvals, return nothing
  if (
    !(
      orgRole &&
      OrganizationUser.ROLES_WITH_EDIT_PERMISSION.includes(orgRole) &&
      features &&
      hasDualApprovals
    )
  ) {
    return null;
  }

  const DualApprovalCheckbox = ({ required, toggle }) => {
    const displayCheckbox = () =>
      organization.hasMultipleUsers ? (
        <Toggle
          testId="dual-approval-checkbox"
          isActive={required}
          onChange={toggle}
        />
      ) : (
        <Tooltip text={l10n.getString("additional-user-tooltip")}>
          <Toggle
            testId="dual-approval-checkbox"
            isActive={required}
            onChange={toggle}
            disabled
          />
        </Tooltip>
      );

    return required ? (
      <Tooltip text={l10n.getString("approval-disable-support")}>
        <Toggle
          testId="dual-approval-checkbox"
          isActive={required}
          onChange={toggle}
        />
      </Tooltip>
    ) : (
      displayCheckbox()
    );
  };

  DualApprovalCheckbox.propTypes = {
    required: PropTypes.bool,
    toggle: PropTypes.func,
  };

  const GRACE_PERIOD_DAYS =
    organization?.ach_dual_approval_expiration_grace_period_days ?? maxDays;

  const renderApprovalSection = (approvalType) => {
    const config = approvalTypes[approvalType];
    const isFeatureEnabled = features[config.featureKey];

    if (!isFeatureEnabled) return null;

    return (
      <div className="margin--bottom--m" key={approvalType}>
        <Row>
          <Row.Item>
            <span className="fontSize--l fontWeight--bold">{config.label}</span>
          </Row.Item>
          <Row.Item shrink>
            <LoadingSkeleton isLoading={loading} lines={1}>
              <DualApprovalCheckbox
                required={organization?.[config.key]}
                toggle={() => handleToggleApproval(approvalType)}
              />
            </LoadingSkeleton>
          </Row.Item>
        </Row>

        {approvalType === "ACH_AND_EXPEDITE" && organization?.[config.key] && (
          <div className={styles.approvalExpirationContainer}>
            <Row>
              <Row.Item>
                <span className="fontWeight--semibold">
                  {l10n.getString("approval-expiration-title")}
                </span>
              </Row.Item>
              <Row.Item shrink>
                <Button
                  kind="negative"
                  size="s"
                  label="Edit expiration"
                  onClick={handleToggleExpirationModal}
                />
              </Row.Item>
            </Row>
            <Row>
              <span className="margin--top--s fontSize--s">
                {l10n.getString("approval-expiration-content-start")}{" "}
                <span className="fontWeight--semibold">
                  {l10n.getString("approval-expiration-days", {
                    days: GRACE_PERIOD_DAYS,
                  })}
                </span>{" "}
                {l10n.getString("approval-expiration-content-end")}
              </span>
            </Row>
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="dual-approvals">
      <ContentCard>
        <h3>{l10n.getString("heading-dual-approvals")}</h3>
        <LoadingSkeleton isLoading={loading} content={"paragraph"}>
          <ReactMarkdown className="margin--bottom--l margin--top--s">
            {l10n.getString("dual-approval-opt-in-description", {
              shortName: short_name,
            })}
          </ReactMarkdown>
        </LoadingSkeleton>

        {Object.keys(approvalTypes).map(renderApprovalSection)}

        <hr />
        <LoadingSkeleton isLoading={loading} content={"paragraph"}>
          <p className="fontSize--xs">
            {l10n.getString("dual-approval-disclosure", {
              shortName: short_name,
            })}
          </p>
        </LoadingSkeleton>
      </ContentCard>

      {activeModal && (
        <ConfirmModal
          dataTestId={approvalTypes[activeModal].testId}
          headerText={l10n.getString("heading-dual-approvals")}
          contentText={l10n.getString("confirm-enable-dual-approvals", {
            shortName: short_name,
          })}
          cancelLabel={l10n.getString("button-cancel")}
          confirmLabel={l10n.getString("button-confirm-turn-on")}
          isOpen={true}
          onConfirm={() => handleConfirmToggle(activeModal)}
          onCancel={closeModal}
          onDismiss={closeModal}
        />
      )}

      <ApprovalExpirationDialog
        isOpen={showApprovalExpirationModal}
        handleClose={handleToggleExpirationModal}
        handleSave={handleSaveExpirationDays}
        gracePeriodDays={GRACE_PERIOD_DAYS}
      />
    </div>
  );
};

const DualApprovalOptInContainer = () => (
  <InstitutionSettingsContextProvider>
    <DualApprovalOptIn />
  </InstitutionSettingsContextProvider>
);

export default DualApprovalOptInContainer;
